import { motion } from "framer-motion";
import {
  FontWeight,
  Glyph,
  HoverEffect,
  Icon,
  JustifyContent,
  MainArea,
  PlainButton,
  Space,
  Visibility,
  XYGrid,
} from "@gocardless/flux-react";
import { Trans } from "@lingui/macro";
import router from "next/router";

import ValueProp from "../ValueProp";

import { routerPush } from "src/common/routing";
import { NavRouteContext } from "src/components/layout/SetupPageLayout";
import { TrackingEvent } from "src/common/trackingEvents";
import { useSegment } from "src/technical-integrations/segment/useSegment";

const variants = {
  hidden: { opacity: 0, x: 200, y: 0 },
  enter: { opacity: 1, x: 0, y: 0 },
  exit: { opacity: 1, x: -100, y: 0 },
};

const transition = {
  type: "spring",
  stiffness: 150,
  damping: 20,
};

interface PageContainerProps {
  imagePath?: string;
  headerText?: string;
  content?: React.ReactNode;
  children: React.ReactNode;
}

const PageContainer: React.FC<PageContainerProps> = ({
  imagePath,
  headerText,
  content,
  children,
}) => {
  const hasValueProp = imagePath || headerText || content;
  const { sendEvent } = useSegment();

  return (
    <motion.div
      initial="hidden"
      animate="enter"
      exit="exit"
      variants={variants}
      transition={transition}
    >
      <XYGrid
        templateAreas={[
          `"form"`,
          null,
          null,
          hasValueProp ? `"form value-prop"` : null,
        ]}
        templateColumns={[
          null,
          null,
          null,
          hasValueProp ? "570px 285px" : null,
          hasValueProp ? "570px 325px" : null,
        ]}
        columnGap={12}
        justifyContent={JustifyContent.Center}
      >
        <MainArea gridArea="form" maxWidth={[null, null, "570px", "none"]}>
          {!!hasValueProp && (
            <NavRouteContext.Consumer>
              {({ backRoute }) => (
                <PlainButton
                  effect={HoverEffect.TextDecoration}
                  weight={FontWeight.Bold}
                  onClick={async () => {
                    sendEvent(
                      TrackingEvent.MERCHANT_ONBOARDING_BACK_BUTTON_CLICKED,
                      {
                        target: backRoute,
                        page: router.pathname,
                      }
                    );

                    routerPush({ route: backRoute });
                  }}
                >
                  <Icon size="12px" name={Glyph.ChevronLeft} />
                  <Space layout="inline" h={0.5} />
                  <Trans id="Back">Back</Trans>
                </PlainButton>
              )}
            </NavRouteContext.Consumer>
          )}
          {children}
        </MainArea>
        {hasValueProp && (
          <Visibility visible={["none", null, null, "block"]}>
            <ValueProp
              imagePath={imagePath}
              headerText={headerText}
              content={content}
            />
          </Visibility>
        )}
      </XYGrid>
    </motion.div>
  );
};

export default PageContainer;
